import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, Newspaper, BookOpen, Mail, ArrowUpRight,
} from "lucide-react";
import { adminMessages, adminNews, adminPages, adminResources } from "../api";
import { Card, PageHeader, Spinner } from "../ui";

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([
      adminPages.list(),
      adminNews.list(),
      adminResources.list(),
      adminMessages.list({ limit: 5 }),
    ])
      .then(([pages, news, resources, msgs]) =>
        setStats({
          pages: pages.length,
          news: news.length,
          newsPublished: news.filter((n) => n.published).length,
          resources: resources.length,
          messages: msgs.total,
          messagesNew: (msgs.items || []).filter((m) => m.status === "new").length,
          recent: msgs.items || [],
        }),
      )
      .catch(() => setStats({ error: true }));
  }, []);

  if (!stats) return <Spinner />;

  const tiles = [
    { label: "Pages", value: stats.pages, sub: "Editable", icon: FileText, href: "/admin/pages" },
    { label: "News posts", value: stats.news, sub: `${stats.newsPublished} published`, icon: Newspaper, href: "/admin/news" },
    { label: "Resources", value: stats.resources, sub: "Documents", icon: BookOpen, href: "/admin/resources" },
    { label: "Messages", value: stats.messages, sub: `${stats.messagesNew} new`, icon: Mail, href: "/admin/messages" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Dashboard" description="A snapshot of the content management system." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <Link key={t.label} to={t.href} className="block">
            <Card className="p-5 transition-all hover:border-[hsl(var(--aoa-royal))] hover:shadow-sm">
              <div className="flex items-start justify-between">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-[hsl(var(--aoa-royal))]/8">
                  <t.icon className="h-5 w-5 text-[hsl(var(--aoa-royal))]" />
                </div>
                <ArrowUpRight className="h-4 w-4 text-slate-400" />
              </div>
              <div className="mt-5 font-display text-3xl font-bold text-[hsl(var(--aoa-navy))]">{t.value}</div>
              <div className="text-xs uppercase tracking-[0.12em] text-slate-500 mt-1">{t.label}</div>
              <div className="text-xs text-slate-400 mt-0.5">{t.sub}</div>
            </Card>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Card className="p-6" testId="dashboard-recent-messages">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))]">Recent messages</h2>
            <Link to="/admin/messages" className="text-xs font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))]">View all →</Link>
          </div>
          {stats.recent.length === 0 ? (
            <p className="text-sm text-slate-500">No messages yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {stats.recent.map((m) => (
                <li key={m.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[hsl(var(--aoa-navy))] truncate">{m.subject}</div>
                    <div className="text-xs text-slate-500 truncate">{m.name} · {m.email}</div>
                  </div>
                  <span className={`flex-none rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${m.status === "new" ? "bg-[hsl(var(--aoa-royal))]/10 text-[hsl(var(--aoa-royal))]" : "bg-slate-100 text-slate-600"}`}>{m.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="p-6">
          <h2 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))]">Quick actions</h2>
          <div className="mt-4 grid gap-2">
            <Link to="/admin/news/new" className="rounded-lg border border-slate-200 px-4 py-3 text-sm hover:border-[hsl(var(--aoa-royal))]">+ New News Post</Link>
            <Link to="/admin/resources/new" className="rounded-lg border border-slate-200 px-4 py-3 text-sm hover:border-[hsl(var(--aoa-royal))]">+ New Resource</Link>
            <Link to="/admin/settings" className="rounded-lg border border-slate-200 px-4 py-3 text-sm hover:border-[hsl(var(--aoa-royal))]">Edit Site Settings</Link>
            <Link to="/admin/pages" className="rounded-lg border border-slate-200 px-4 py-3 text-sm hover:border-[hsl(var(--aoa-royal))]">Edit Pages</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
